// Compact quick-entry slide-in for "+ New Incident" — fast 3-field create.
// Full multi-step registration lives at /incidents/new.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useStore } from "../store";
import { templates, templatePartiesMap, severityColor } from "../data/mockData";
import { useCreateIncident } from "../api/sitroom";

const SEV_OPTIONS = [
  { id: "s1", label: "S1", sub: "Line stop" },
  { id: "s2", label: "S2", sub: "<4h impact" },
  { id: "s3", label: "S3", sub: "Degraded" },
  { id: "s4", label: "S4", sub: "Risk notice" },
];

export const NewIncidentModal = () => {
  const isOpen = useStore((s) => s.isModalOpen);
  const closeModal = useStore((s) => s.closeModal);
  const openPanel = useStore((s) => s.openPanel);
  const addToast = useStore((s) => s.addToast);
  const navigate = useNavigate();
  const createIncident = useCreateIncident();

  const [tpl, setTpl] = useState("");
  const [title, setTitle] = useState("");
  const [sev, setSev] = useState("s2");
  const [titleError, setTitleError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTpl("");
      setTitle("");
      setSev("s2");
      setTitleError(false);
      setBusy(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const found = templates.find((t) => t.value === tpl);
    if (found && found.sev) setSev(found.sev);
  }, [tpl]);

  if (!isOpen) return null;

  const linkedParties = templatePartiesMap[tpl] || [];

  const handleSubmit = async () => {
    if (title.trim().length < 5) {
      setTitleError(true);
      return;
    }
    setBusy(true);
    try {
      const created = await createIncident.mutateAsync({
        title: title.trim(),
        template: templates.find((t) => t.value === tpl)?.label || "Quick entry",
        templateKey: tpl,
        severity: sev,
        status: "open",
        sla: 0,
        age: "0m",
        description: "",
        parties: linkedParties.join(" ↔ "),
        opener: "G. Castro",
        linkedParties: linkedParties.map((name) => ({
          name, role: name, color: severityColor(sev),
        })),
        timeline: [],
        comments: [],
        attachments: [],
      });
      addToast({
        type: "info",
        title: "Quick incident opened",
        body: `${created.id} — add details from the panel`,
        action: { label: "View →", onClick: () => openPanel(created.id) },
      });
      closeModal();
      setTimeout(() => openPanel(created.id), 250);
    } catch (e) {
      addToast({
        type: "info",
        title: "Failed",
        body: e?.response?.data?.detail || "Could not create incident",
      });
      setBusy(false);
    }
  };

  const switchToFull = () => {
    closeModal();
    navigate("/incidents/new");
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[9990]"
        style={{ background: "rgba(8,9,13,0.65)", backdropFilter: "blur(4px)" }}
        onClick={closeModal}
        data-testid="modal-backdrop"
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-[9991]"
        style={{
          width: "min(440px, 92vw)",
          background: "#0d0f18",
          borderLeft: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.55)",
        }}
        data-testid="new-incident-modal"
      >
        <div
          className="flex items-center justify-between px-5"
          style={{ height: 56, borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div>
            <div
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: 16,
                fontWeight: 600,
                color: "#f0f2f8",
                lineHeight: 1.2,
              }}
            >
              Quick entry
            </div>
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "#9198b0",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
              }}
            >
              Add details after opening
            </div>
          </div>
          <button
            onClick={closeModal}
            className="p-1.5 rounded transition-colors"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#9198b0", cursor: "pointer" }}
            data-testid="modal-close-btn"
          >
            <X size={15} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto" style={{ height: "calc(100% - 56px)", paddingBottom: 100 }}>
          {/* Template */}
          <div className="mb-4">
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Template
            </div>
            <select
              value={tpl}
              onChange={(e) => setTpl(e.target.value)}
              className="w-full outline-none"
              style={{
                background: "#08090d",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#f0f2f8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                padding: "8px 10px",
                borderRadius: 6,
              }}
              data-testid="modal-template-select"
            >
              {templates.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="mb-4">
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Title
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (titleError && e.target.value.trim().length >= 5) setTitleError(false);
              }}
              placeholder="Short summary…"
              className="w-full outline-none"
              style={{
                background: "#08090d",
                border: `1px solid ${titleError ? "#E24B4A" : "rgba(255,255,255,0.10)"}`,
                color: "#f0f2f8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 13,
                padding: "8px 10px",
                borderRadius: 6,
              }}
              data-testid="modal-title-input"
            />
            {titleError && (
              <div
                className="mt-1"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#E24B4A" }}
              >
                Title must be at least 5 characters
              </div>
            )}
          </div>

          {/* Severity */}
          <div className="mb-4">
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Severity
            </div>
            <div className="grid grid-cols-4 gap-2">
              {SEV_OPTIONS.map((s) => {
                const active = sev === s.id;
                const c = severityColor(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSev(s.id)}
                    className="px-2 py-2 rounded text-center transition-colors"
                    style={{
                      background: active ? `${c}1f` : "rgba(255,255,255,0.04)",
                      border: `1px solid ${active ? c : "rgba(255,255,255,0.10)"}`,
                      color: active ? c : "#c5cad8",
                      fontFamily: "'DM Mono', monospace",
                      cursor: "pointer",
                    }}
                    data-testid={`modal-sev-${s.id}`}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 9, color: "#6b7388", letterSpacing: "0.5px", marginTop: 2 }}>
                      {s.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Switch to full */}
          <button
            type="button"
            onClick={switchToFull}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded transition-colors"
            style={{
              background: "rgba(24,95,165,0.06)",
              border: "1px dashed rgba(24,95,165,0.45)",
              color: "#9ec3ec",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
            data-testid="modal-switch-to-full"
          >
            Switch to full registration <ArrowRight size={13} />
          </button>

          {/* Action buttons — inside body to avoid Emergent badge overlap at corner */}
          <div className="flex items-center gap-2 mt-5">
            <button
              onClick={closeModal}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded transition-colors"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#c5cad8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                letterSpacing: "1px",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
              data-testid="modal-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={busy}
              className="flex-1 px-3 py-2 rounded transition-all"
              style={{
                background: busy ? "#0c4076" : "#185FA5",
                border: "1px solid #185FA5",
                color: "#fff",
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: "0.5px",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.85 : 1,
              }}
              data-testid="modal-submit-btn"
            >
              {busy ? "Opening…" : "Open incident"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
